import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { createOrder } from '../services/api';
import { toast } from 'react-toastify';
import TopNavbar from '../components/TopNavbar';
import MainHeader from '../components/MainHeader';
import Footer from '../components/Footer';

const CheckoutHeader = () => {
  return (
    <div className="relative bg-gradient-to-r from-green-500 to-green-600 py-12 sm:py-16">
      <div className="absolute inset-0 bg-black bg-opacity-20"></div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <nav className="flex justify-center items-center space-x-2 mb-4 sm:mb-6">
          <a href="/" className="text-white hover:text-green-200 transition-colors">Home</a>
          <span className="text-white">/</span>
          <a href="/cart" className="text-white hover:text-green-200 transition-colors">Cart</a>
          <span className="text-white">/</span>
          <span className="text-white font-medium">Checkout</span>
        </nav>
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2 sm:mb-4">Checkout</h1>
        <p className="text-lg sm:text-xl text-white opacity-90 max-w-2xl mx-auto">
          Complete your order via WhatsApp
        </p>
      </div>
    </div>
  );
};

const PaymentNotification = ({ message, type, onClose }) => {
  if (!message) return null;

  const bgColor = type === 'success' ? 'bg-green-100 text-green-800' : 
                  type === 'error' ? 'bg-red-100 text-red-800' : 
                  'bg-blue-100 text-blue-800';

  return (
    <div className={`${bgColor} px-6 py-4 rounded-lg mb-6 flex items-center justify-between`}>
      <div className="flex items-center">
        <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        <span>{message}</span>
      </div>
      <button onClick={onClose} className="ml-4">
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, onClose, onConfirm, orderDetails }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">Complete Order via WhatsApp?</h3>
          
          <div className="text-sm text-gray-500 mb-6 space-y-2">
            <p>Are you sure you want to complete this order?</p>
            <p>Total: <span className="font-bold text-green-600">KES {orderDetails.totalAmount?.toLocaleString()}</span></p>
            <p>Items: <span className="font-medium">{orderDetails.itemCount}</span></p>
            <p>WhatsApp will open with all your order details pre-filled.</p>
          </div>
          
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Yes, Open WhatsApp
            </button>
          </div>
          
          <p className="text-xs text-gray-400 mt-4">
            Click "Yes, Open WhatsApp" to proceed. The order will be saved and WhatsApp will open with all details.
          </p>
        </div>
      </div>
    </div>
  );
};

// WhatsApp Order Component - Replaces M-Pesa Payment
const WhatsAppOrder = ({ 
  items, 
  totalAmount, 
  customerInfo, 
  onOrderSuccess,
  onOrderError 
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderStatus, setOrderStatus] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // WhatsApp business phone number - Updated to your number
  const whatsappNumber = '254714139461'; // Format: 254XXXXXXXXX (Kenya)
  
  // Generate formatted order details for WhatsApp message
  const generateOrderMessage = () => {
    const orderDate = new Date().toLocaleString('en-KE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    // Format customer details
    const customerDetails = `
👤 *CUSTOMER DETAILS*
• Name: ${customerInfo.firstName} ${customerInfo.lastName}
• Email: ${customerInfo.email}
• Phone: ${customerInfo.phone}
• Location: ${customerInfo.city}, ${customerInfo.county}
${customerInfo.postalCode ? `• Postal Code: ${customerInfo.postalCode}` : ''}
    `.trim();
    
    // Format order items with individual prices
    let itemsDetails = '🛒 *ORDER ITEMS*\n';
    items.forEach((item, index) => {
      const itemTotal = item.price * item.quantity;
      itemsDetails += `
${index + 1}. ${item.name}
   └ Quantity: ${item.quantity}
   └ Price per item: KES ${item.price.toLocaleString()}
   └ Subtotal: KES ${itemTotal.toLocaleString()}
      `.trim() + '\n\n';
    });
    
    // Format order summary - REMOVED VAT AND SHIPPING LINES
    const orderSummary = `
💰 *ORDER SUMMARY*
• Subtotal (${items.length} items): KES ${getTotalPrice().toLocaleString()}
────────────────────
• *TOTAL AMOUNT: KES ${totalAmount.toLocaleString()}*
    `.trim();
    
    // Full message
    const message = `
📦 *NEW ORDER REQUEST*
────────────────────
${customerDetails}

${itemsDetails}

${orderSummary}

📅 *Order Date:* ${orderDate}

_This order was placed through the online store._
_Please confirm availability and provide payment instructions._
    `.trim();
    
    return encodeURIComponent(message);
  };
  
  const getTotalPrice = () => {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };
  
  const processOrder = async () => {
    setIsProcessing(true);
    setOrderStatus('Preparing your order details...');
    
    try {
      // Validate customer information
      if (!customerInfo.firstName || !customerInfo.lastName || !customerInfo.email || !customerInfo.phone || !customerInfo.city) {
        throw new Error('Please fill in all required customer information');
      }
      
      setOrderStatus('Saving order to database...');
      
      // Create the order in database first
      const orderData = {
        items: items.map(item => ({
          item: item.id,
          quantity: item.quantity
        })),
        shipping_info: {
          first_name: customerInfo.firstName,
          last_name: customerInfo.lastName,
          email: customerInfo.email,
          phone: customerInfo.phone,
          address: `${customerInfo.city}${customerInfo.county ? ', ' + customerInfo.county : ''}`,
          city: customerInfo.city,
          county: customerInfo.county || customerInfo.city,
          postal_code: customerInfo.postalCode || '00000'
        },
        total_price: Number(totalAmount.toFixed(2)),
        payment_method: 'whatsapp',
        status: 'pending'
      };
      
      const token = await getToken();
      const order = await createOrder(orderData, token);
      
      if (!order || !order.id) {
        throw new Error('Failed to create order record');
      }
      
      setOrderStatus('Generating WhatsApp message...');
      
      // Generate WhatsApp message with order ID
      const message = generateOrderMessage() + `\n\n📋 *Order ID:* #${order.id}`;
      
      // Open WhatsApp with pre-filled message
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;
      
      setOrderStatus('Opening WhatsApp...');
      
      // Open in new tab
      window.open(whatsappUrl, '_blank');
      
      // Show success message
      setOrderStatus('✓ WhatsApp opened! Please send the message to complete your order.');
      toast.success('✓ WhatsApp opened! Please send the message to confirm your order.');
      
      // Call success callback
      onOrderSuccess(order);
      
      setIsProcessing(false);
      
    } catch (error) {
      console.error('Error processing WhatsApp order:', error);
      setOrderStatus(`✗ Error: ${error.message}`);
      onOrderError(error.message);
      setIsProcessing(false);
    }
  };
  
  const handleWhatsAppOrder = () => {
    // Show confirmation modal first
    setShowConfirmation(true);
  };
  
  const handleConfirmOrder = () => {
    setShowConfirmation(false);
    processOrder();
  };
  
  const getToken = async () => {
    // This would come from your auth context
    const token = localStorage.getItem('token') || '';
    return token;
  };
  
  return (
    <>
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirmOrder}
        orderDetails={{
          totalAmount: totalAmount,
          itemCount: items.length
        }}
      />
      
      <div className="bg-white rounded-lg p-6 shadow-sm border relative">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <span className="mr-3 text-green-600">💬</span>
          Complete Order via WhatsApp
        </h3>

        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h4 className="font-semibold text-green-800 mb-2">📱 How it works:</h4>
          <ol className="text-sm text-green-700 space-y-1">
            <li>1. Fill in your contact details above</li>
            <li>2. Click "Complete Order via WhatsApp"</li>
            <li>3. Confirm your order in the popup</li>
            <li>4. WhatsApp will open with all order details</li>
            <li>5. Send the message to confirm your order</li>
            <li>6. We'll contact you for payment details</li>
          </ol>
        </div>

        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Items Total:</span>
              <span className="font-medium">KES {getTotalPrice().toLocaleString()}</span>
            </div>
            {/* REMOVED VAT AND SHIPPING FROM DISPLAY */}
            <hr className="my-2" />
            <div className="flex justify-between items-center">
              <span className="text-gray-600 font-semibold">Total Amount:</span>
              <span className="text-2xl font-bold text-green-600">KES {totalAmount.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {orderStatus && (
          <div className={`mb-6 p-4 rounded-lg border ${
            orderStatus.includes('✓') 
              ? 'bg-green-50 border-green-200 text-green-800' 
              : orderStatus.includes('✗')
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}>
            <p className="text-sm flex items-center">
              {orderStatus.includes('✓') ? (
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : orderStatus.includes('✗') ? (
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              )}
              {orderStatus}
            </p>
          </div>
        )}

        <button
          onClick={handleWhatsAppOrder}
          disabled={isProcessing || 
                   !customerInfo.firstName || 
                   !customerInfo.lastName || 
                   !customerInfo.email || 
                   !customerInfo.phone || 
                   !customerInfo.city}
          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white py-3 px-6 rounded-lg font-medium transition-colors flex items-center justify-center"
        >
          {isProcessing ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </>
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.76.982.998-3.675-.236-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.9 6.994c-.004 5.45-4.438 9.88-9.888 9.88m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.333.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.333 11.893-11.893 0-3.18-1.264-6.177-3.557-8.44"/>
              </svg>
              Complete Order via WhatsApp
            </>
          )}
        </button>

        <p className="text-xs text-gray-500 mt-4 text-center">
          You'll be asked to confirm before WhatsApp opens with all your order details pre-filled.
        </p>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="font-medium text-gray-900 mb-2">📋 Order Includes:</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li className="flex items-center">
              <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              All product details with individual prices
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Item quantities and subtotals
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Your contact information
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Total order amount
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Delivery address
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Order date, time and ID
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

const Checkout = () => {
  const { items, getTotalPrice, clearCart } = useCart();
  const { getToken } = useAuth();

  // Common Kenyan cities/towns for quick selection
  const kenyaCities = [
    'Nairobi',
    'Mombasa',
    'Kisumu',
    'Nakuru',
    'Eldoret',
    'Thika',
    'Malindi',
    'Kitale',
    'Garissa',
    'Machakos',
    'Nyeri',
    'Meru',
    'Naivasha',
    'Kericho',
    'Kakamega',
    'Embu',
    'Lamu'
  ];

  // All 47 Kenyan counties
  const kenyaCounties = [
    'Mombasa',
    'Kwale',
    'Kilifi',
    'Tana River',
    'Lamu',
    'Taita-Taveta',
    'Garissa',
    'Wajir',
    'Mandera',
    'Marsabit',
    'Isiolo',
    'Meru',
    'Tharaka-Nithi',
    'Embu',
    'Kitui',
    'Machakos',
    'Makueni',
    'Nyandarua',
    'Nyeri',
    'Kirinyaga',
    'Murang\'a',
    'Kiambu',
    'Turkana',
    'West Pokot',
    'Samburu',
    'Trans Nzoia',
    'Uasin Gishu',
    'Elgeyo-Marakwet',
    'Nandi',
    'Baringo',
    'Laikipia',
    'Nakuru',
    'Narok',
    'Kajiado',
    'Kericho',
    'Bomet',
    'Kakamega',
    'Vihiga',
    'Bungoma',
    'Busia',
    'Siaya',
    'Kisumu',
    'Homa Bay',
    'Migori',
    'Kisii',
    'Nyamira',
    'Nairobi'
  ];

  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: 'Nairobi',
    county: 'Nairobi',
    postalCode: ''
  });
  const [notification, setNotification] = useState({ message: '', type: '' });

  const shipping = 0;
  const tax = getTotalPrice() * 0.16; // 16% VAT - Still used for display only
  const finalTotal = getTotalPrice() + shipping + tax;

  const handleInputChange = (e) => {
    setCustomerInfo({
      ...customerInfo,
      [e.target.name]: e.target.value
    });
  };

  const handleOrderSuccess = (order) => {
    setNotification({
      message: `✓ Order #${order.id} created successfully! WhatsApp has been opened with all your order details. Please send the message to confirm your order.`,
      type: 'success'
    });
    
    // Clear cart after successful order creation
    setTimeout(() => {
      clearCart();
      toast.success('✓ Your order has been processed! Check your WhatsApp.');
    }, 2000);
  };

  const handleOrderError = (error) => {
    setNotification({
      message: `✗ ${error}`,
      type: 'error'
    });
  };

  if (items.length === 0) {
    return (
      <div className="App">
        <TopNavbar />
        <MainHeader />
        <CheckoutHeader />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
            <p className="text-gray-600 mb-8">Add some items to your cart before proceeding to checkout.</p>
            <a
              href="/shop"
              className="inline-block bg-green-500 hover:bg-green-600 text-white py-3 px-8 rounded-lg font-medium transition-colors"
            >
              Continue Shopping
            </a>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <TopNavbar />
      <MainHeader />
      <CheckoutHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <PaymentNotification 
          message={notification.message} 
          type={notification.type}
          onClose={() => setNotification({ message: '', type: '' })}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Customer Information */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm border">
              <h3 className="text-xl font-bold text-gray-900 mb-6">📋 Customer Information</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={customerInfo.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={customerInfo.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={customerInfo.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={customerInfo.phone}
                    onChange={handleInputChange}
                    placeholder="0714139461"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <select
                    name="city"
                    value={customerInfo.city}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                    required
                  >
                    {kenyaCities.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    County *
                  </label>
                  <select
                    name="county"
                    value={customerInfo.county}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                    required
                  >
                    {kenyaCounties.map((county) => (
                      <option key={county} value={county}>
                        {county}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={customerInfo.postalCode}
                    onChange={handleInputChange}
                    placeholder="00100"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-sm flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Your information will be securely stored and sent via WhatsApp for order processing
                </p>
              </div>
            </div>

            {/* WhatsApp Order Component */}
            <WhatsAppOrder 
              items={items}
              totalAmount={getTotalPrice()} // Use subtotal without VAT
              customerInfo={customerInfo}
              onOrderSuccess={handleOrderSuccess}
              onOrderError={handleOrderError}
            />
          </div>

          {/* Order Summary */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm border">
              <h3 className="text-xl font-bold text-gray-900 mb-6">📦 Order Summary</h3>
              
              <div className="space-y-4 mb-6 max-h-96 overflow-y-auto pr-2">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center space-x-4 p-3 hover:bg-gray-50 rounded-lg">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">{item.name}</h4>
                      <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                      <p className="text-sm text-gray-600">
                        Price per item: KES {item.price.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <p className="font-medium text-green-600">
                        KES {(item.price * item.quantity).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <hr className="my-6" />
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal ({items.length} items)</span>
                  <span className="font-medium">KES {getTotalPrice().toLocaleString()}</span>
                </div>
                {/* REMOVED SHIPPING LINE */}
                {/* REMOVED VAT LINE */}
                <hr className="my-4 border-dashed" />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total Amount</span>
                  <span className="text-green-600 text-xl">KES {getTotalPrice().toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zm7-10a1 1 0 01.707.293l.707.707.707-.707A1 1 0 0115.657 2H17a1 1 0 110 2h-.343l-.707.707a1 1 0 01-1.414 0l-.707-.707A1 1 0 0113 4h-1a1 1 0 010-2h1a1 1 0 01.293-.707z" clipRule="evenodd" />
                </svg>
                Why Order via WhatsApp?
              </h4>
              <ul className="text-sm text-green-700 space-y-2">
                <li className="flex items-start">
                  <svg className="w-4 h-4 mr-2 mt-0.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Instant confirmation and communication</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 mr-2 mt-0.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Secure payment arrangements</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 mr-2 mt-0.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Real-time order updates</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 mr-2 mt-0.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Quick responses to your questions</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 mr-2 mt-0.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Flexible payment options (M-Pesa, bank transfer, etc.)</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" clipRule="evenodd" />
                </svg>
                📞 Need Help?
              </h4>
              <p className="text-sm text-blue-700 mb-2">
                If you have any questions about your order, feel free to message us directly on WhatsApp:
              </p>
              <div className="bg-white p-3 rounded-lg border border-blue-100">
                <p className="font-medium text-green-700 text-center">
                  <a 
                    href={`https://wa.me/254714139461`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    📱 +254 714 139 461
                  </a>
                </p>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                Click the number above to chat with us directly on WhatsApp
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Checkout;